"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** Prevent closing on overlay click */
  persistent?: boolean;
}

function Modal({ open, onClose, children, className, persistent }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  /*
    Native <dialog>.close() snaps the dialog away with no animation. To get
    a real exit transition we keep the <dialog> mounted while AnimatePresence
    plays the panel's `exit` prop, then call `.close()` from the
    onExitComplete callback. `shouldRender` lags `open` on the way out so
    the dialog stays in the tree during the ~180ms exit.
  */
  const [shouldRender, setShouldRender] = useState(open);

  useEffect(() => {
    if (open) setShouldRender(true);
  }, [open]);

  // Sync `open` prop → native dialog state, but only on the open path.
  // The close path is handled in `handleExitComplete` so the exit animation
  // has time to play.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !shouldRender) return;
    if (open && !dialog.open) {
      lastFocusedRef.current = document.activeElement as HTMLElement;
      dialog.showModal();
    }
  }, [open, shouldRender]);

  // Lock body scroll while open. Native <dialog> doesn't block scrolling of
  // the underlying page, so long modals would let the page scroll behind.
  useEffect(() => {
    if (!open) return;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    // Reserve space so the layout doesn't shift when the scrollbar is hidden.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  // Handle Escape (dialog handles this natively) and backdrop click
  const handleCancel = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (!persistent) onClose();
    },
    [onClose, persistent]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (persistent) return;
      if (e.target === dialogRef.current) {
        onClose();
      }
    },
    [onClose, persistent]
  );

  // After the panel's exit animation finishes, actually close the
  // <dialog> and unmount. Restore focus to whatever was focused before
  // the modal opened — same behaviour as the previous instant close.
  const handleExitComplete = useCallback(() => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    setShouldRender(false);
    lastFocusedRef.current?.focus();
    lastFocusedRef.current = null;
  }, []);

  if (!shouldRender) return null;

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      className={cn(
        // Overlay
        "fixed inset-0 z-[var(--z-modal)] m-0 h-full w-full max-h-full max-w-full",
        "bg-transparent backdrop:bg-[var(--color-bg-overlay)]",
        "flex items-center justify-center p-[var(--space-4)]",
        // Backdrop fade-in stays via Tailwind's `open:` selector. We don't
        // animate the backdrop out — Motion controls the panel exit, and
        // the backdrop snaps away when .close() fires post-exit, which is
        // brief enough to read as the panel "leaving the page".
        "open:animate-in open:fade-in-0",
      )}
      aria-modal="true"
    >
      <AnimatePresence onExitComplete={handleExitComplete}>
        {open && (
          <motion.div
            key="modal-panel"
            initial={{ opacity: 0, scale: 0.96, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 4 }}
            transition={{
              type: "spring",
              stiffness: 360,
              damping: 32,
              mass: 0.8,
            }}
            className={cn(
              "relative w-full max-w-lg",
              "rounded-[var(--radius-xl)] border border-[var(--color-border-default)]",
              "bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] shadow-[var(--shadow-xl)]",
              "flex flex-col max-h-[calc(100dvh-var(--space-8))]",
              className
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </dialog>
  );
}

/* ── Subcomponents ──────────────────────────────────────────── */

function ModalHeader({
  className,
  children,
  onClose,
}: React.ComponentProps<"div"> & { onClose?: () => void }) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-[var(--space-4)]",
        "px-[var(--space-5)] pt-[var(--space-5)] pb-[var(--space-3)]",
        className
      )}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="shrink-0 rounded-[var(--radius-md)] p-[var(--space-1)] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-muted)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]"
          aria-label="Close"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function ModalTitle({ className, ...props }: React.ComponentProps<"h2">) {
  return (
    <h2
      className={cn(
        "font-heading text-[length:var(--font-size-lg)] font-[number:var(--font-weight-semibold)] tracking-[var(--letter-spacing-tight)]",
        "text-[var(--color-text-primary)]",
        className
      )}
      {...props}
    />
  );
}

function ModalDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn(
        "text-[length:var(--font-size-sm)] text-[var(--color-text-muted)] mt-[var(--space-1)]",
        className
      )}
      {...props}
    />
  );
}

function ModalBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex-1 overflow-y-auto px-[var(--space-5)] py-[var(--space-3)]",
        className
      )}
      {...props}
    />
  );
}

function ModalFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-[var(--space-2)] sm:flex-row sm:items-center sm:justify-end sm:gap-[var(--space-3)]",
        "[&>button]:w-full sm:[&>button]:w-auto",
        "border-t border-[var(--color-border-subtle)]",
        "px-[var(--space-5)] py-[var(--space-4)]",
        className
      )}
      {...props}
    />
  );
}

export {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
};
export type { ModalProps };
